/**
 * Rounds up the provided CO2 emissions value to the nearest whole number.
 *
 * The function takes a numeric value representing CO2 emissions and applies the
 * mathematical ceiling operation to ensure the result is the smallest integer
 * greater than or equal to the given value.
 *
 * @param {number} co2 - The CO2 emissions value to be rounded up.
 * @returns {number} - The rounded-up CO2 emissions value as an integer.
 */
export const roundCo2E = (co2: number): number => Math.ceil(co2)

/**
 * Rounds a given distance in kilometers up to the nearest whole number.
 *
 * @param {number} km - The distance in kilometers to be rounded up.
 * @returns {number} The rounded-up value of the input distance.
 */
export const roundKm = (km: number): number => Math.ceil(km)

/**
 * Calculates and rounds up the CO₂ emissions per person by dividing the total CO₂ emissions
 * by the number of people and rounding the result to the nearest whole number.
 *
 * @param {number} co2Total - The total CO₂ emissions.
 * @param {number} countPerson - The number of people to divide the CO₂ emissions among.
 * @returns {number} The rounded up CO₂ emissions per person.
 */
export const roundCo2EPerPerson = (co2Total: number, countPerson: number): number => Math.ceil(co2Total / countPerson)

/**
 * Rounds a given number to one decimal place and returns it as a string.
 *
 * The function takes a numerical input and utilizes the `toFixed` method
 * to format the number with a single digit after the decimal point.
 *
 * @param {number} value - The numeric value to be rounded.
 * @returns {string} - The rounded number as a string, with one decimal point precision.
 */
export const roundRelativeNumber = (value: number): string => value.toFixed(1)


/**
 * Rounds a given number to one decimal place.
 *
 * @param {number} number - The number to be rounded.
 * @returns {number} The given number rounded to one decimal place.
 */
export const roundNumberToOneDigit = (number: number): number => {
    let power = Math.pow(10, 1);
    let result = Math.round(number * power) / power;
    return result
}